import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { RmEvaluationModal }       from "./RmEvaluationModal";
import { RmEvaluationDetailModal } from "./RmEvaluationDetailModal";
import { MonthYearSelect }         from "../../components/MonthYearSelect";
import { currentMonthKey }         from "../../utils/dateUtils";
import { useToast } from "../../components/Toast";

const STATUS_CFG = {
  APPROVED:             { bg:"#d1fae5", color:"#065f46", border:"#6ee7b7", label:"Approved" },
  NEED_FURTHER_STUDY:   { bg:"#fef9c3", color:"#854d0e", border:"#fde047", label:"Need Further Study" },
  UNDER_OBSERVATION:    { bg:"#e0f2fe", color:"#0369a1", border:"#7dd3fc", label:"Under Observation" },
  REJECTED:             { bg:"#fee2e2", color:"#991b1b", border:"#fca5a5", label:"Rejected" },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] ?? { bg:"#f3f4f6", color:"#6b7280", border:"#d1d5db", label: status };
  return (
    <span style={{ background:c.bg, color:c.color, border:`1px solid ${c.border}`,
      borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:600 }}>
      {c.label}
    </span>
  );
}

export function PageRdRmEvaluation({ rms = [], myPerms = [] }) {
  const showToast = useToast();
  const canEdit   = myPerms.includes("QC_EDIT") || myPerms.includes("RND_EDIT");
  const canDelete = myPerms.includes("USER_MANAGE");

  const [evals,    setEvals]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState("");
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [filterStatus, setFilterStatus] = useState("");
  const [creating,     setCreating]     = useState(false);
  const [selected,     setSelected]     = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setEvals(await api.fetchRmEvaluations());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return evals.filter(e => {
      if ((e.date || "").startsWith(monthKey) === false && monthKey) return false;
      if (filterStatus && e.status !== filterStatus) return false;
      if (q && !e.evalNo?.toLowerCase().includes(q) &&
               !e.rmId?.toLowerCase().includes(q) &&
               !e.rmName?.toLowerCase().includes(q) &&
               !e.rmCode?.toLowerCase().includes(q) &&
               !e.supplier?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [evals, search, monthKey, filterStatus]);

  async function handleCreate(body) {
    const created = await api.createRmEvaluation(body);
    setEvals(old => [created, ...old]);
    setCreating(false);
    showToast("Evaluation created");
  }

  async function handleUpdate(id, body) {
    const updated = await api.updateRmEvaluation(id, body);
    setEvals(old => old.map(e => e.id === id ? updated : e));
    if (selected?.id === id) setSelected(updated);
    showToast("Evaluation updated");
    return updated;
  }

  async function handleDelete(id) {
    await api.deleteRmEvaluation(id);
    setEvals(old => old.filter(e => e.id !== id));
    setSelected(null);
    showToast("Evaluation deleted", "info");
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">RM Evaluation</div>
          <div className="page-subtitle">New and alternate raw material evaluation records</div>
        </div>
        <div className="page-header-right">
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New Evaluation</button>
          )}
        </div>
      </div>

      <div className="action-bar">
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ width:180 }}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_CFG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input className="form-input" placeholder="Search eval no, RM, supplier…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:260 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Eval No</th>
                <th>Type</th>
                <th>RM Code / Name</th>
                <th>Date</th>
                <th>Supplier</th>
                <th>Purpose</th>
                <th>Parameters</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.id} style={{ cursor:"pointer" }}
                  onClick={() => setSelected(row)}>
                  <td className="fw-600">{row.evalNo}</td>
                  <td>
                    <span style={{ fontSize:11, padding:"1px 6px", borderRadius:3,
                      background: row.evalType === "ALTERNATE" ? "#ede9fb" : "#fef9c3",
                      color:      row.evalType === "ALTERNATE" ? "#6b21a8" : "#854d0e",
                      fontWeight: 600 }}>
                      {row.evalType === "ALTERNATE" ? "Alternate" : "New RM"}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight:600, fontSize:13 }}>
                      {row.evalType === "ALTERNATE" ? row.rmId : row.rmCode || "—"}
                    </div>
                    <div style={{ fontSize:11, color:"#6b7280" }}>
                      {row.evalType === "ALTERNATE"
                        ? (rms.find(r => r.id === row.rmId)?.name || row.rmId)
                        : row.rmName || "—"}
                    </div>
                  </td>
                  <td style={{ whiteSpace:"nowrap" }}>{row.date}</td>
                  <td>{row.supplier || "—"}</td>
                  <td style={{ maxWidth:200, fontSize:12, color:"#6b7280" }}>{row.purpose || "—"}</td>
                  <td style={{ fontSize:12, color:"#6b7280" }}>
                    {(row.params?.length ?? 0) > 0
                      ? `${row.params.length} param${row.params.length > 1 ? "s" : ""}`
                      : <span style={{ color:"#aaa" }}>—</span>}
                  </td>
                  <td><StatusBadge status={row.status} /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign:"center", color:"#aaa", padding:24 }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreating(false); }}>
          <RmEvaluationModal
            rms={rms}
            onSave={handleCreate}
            onCancel={() => setCreating(false)}
          />
        </div>
      )}

      {selected && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <RmEvaluationDetailModal
            evaluation={selected}
            rms={rms}
            canEdit={canEdit}
            canDelete={canDelete}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onClose={() => setSelected(null)}
          />
        </div>
      )}
    </>
  );
}
